<template>
  <section class="account-mfa">
    <div class="account-mfa__header">
      <div>
        <h3 class="account-mfa__title">
          Multi-factor authentication
        </h3>
        <p class="account-mfa__description">
          Use an authenticator app to verify sign-ins. Email codes stay available as a fallback.
        </p>
      </div>
      <RsButton
        class="account-mfa__add"
        variant="secondary"
        hollow
        icon="far fa-plus mr-2"
        @click="isAddOpen = true"
      >
        Add authenticator
      </RsButton>
    </div>

    <ul class="account-mfa__list">
      <li class="account-mfa__item">
        <span class="account-mfa__icon"><i class="far fa-envelope" /></span>
        <span class="account-mfa__text">
          <span class="account-mfa__name">Email</span>
          <span class="account-mfa__meta">Codes sent to {{ email }}</span>
        </span>
        <span class="account-mfa__badge">Always available</span>
      </li>
      <li
        v-for="authenticator in authenticators"
        :key="authenticator.id"
        class="account-mfa__item"
      >
        <span class="account-mfa__icon"><i class="far fa-mobile" /></span>
        <span class="account-mfa__text">
          <span class="account-mfa__name">{{ authenticator.identifier }}</span>
          <span class="account-mfa__meta">Added {{ formatAddedDate(authenticator.createdDate) }}</span>
        </span>
        <button
          type="button"
          class="account-mfa__remove"
          @click="confirmRemove(authenticator)"
        >
          Remove
        </button>
      </li>
    </ul>

    <RsModalAddAuthenticator
      v-model="isAddOpen"
      @added="loadAuthenticators"
    />
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import { IAuthenticator, getAuthenticators, removeAuthenticator } from '@/shared/services/mfa';
import RsModalAddAuthenticator from '@/components/RsModalAddAuthenticator.vue';

export default defineComponent({
  name: 'RsPartialAccountMfa',

  components: { RsModalAddAuthenticator },

  props: {
    email: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      authenticators: [] as IAuthenticator[],
      isAddOpen: false,
    };
  },

  computed: {
    userId(): number {
      return Number(this.$rsGlobals.$currentUser.userId);
    },
  },

  mounted() {
    this.loadAuthenticators();
  },

  methods: {
    async loadAuthenticators() {
      try {
        const authenticators = await getAuthenticators(this.userId);
        this.authenticators = [ ...authenticators ].sort((a, b) => dayjs(a.createdDate).valueOf() - dayjs(b.createdDate).valueOf());
      } catch {
        // The shared request handler already shows the error; keep the list as it was.
      }
    },

    formatAddedDate(date: string): string {
      return dayjs(date).format('MMM D, YYYY');
    },

    async confirmRemove(authenticator: IAuthenticator) {
      const name = authenticator.identifier.replace(/^Authenticator:\s*/, '');
      // Centered confirm with a red badge icon, as in the design (the shared remove popup is left-aligned).
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        iconHtml: '<i class="far fa-exclamation-triangle"></i>',
        title: 'Remove authenticator?',
        // `text`, not `html`: the device name is user input.
        text: `${name} will no longer be accepted at sign-in. If it was your only authenticator, you'll verify with email codes instead.`,
        // The app's global popup styles already show Cancel before the confirm button.
        showCancelButton: true,
        focusCancel: true,
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        buttonsStyling: false,
        customClass: {
          popup: 'remove-authenticator-popup',
          icon: 'remove-authenticator-popup__icon',
          title: 'remove-authenticator-popup__title',
          htmlContainer: 'remove-authenticator-popup__text',
          actions: 'remove-authenticator-popup__actions',
          confirmButton: 'remove-authenticator-popup__confirm',
          cancelButton: 'remove-authenticator-popup__cancel',
        },
      });
      if (!isConfirmed) return;

      try {
        await removeAuthenticator(authenticator.id);
      } catch {
        // The shared request handler already shows the error; the device stays in the list.
        return;
      }
      this.authenticators = this.authenticators.filter(({ id }) => id !== authenticator.id);
      this.$eventBus.emit('show-success-toast', { message: 'Authenticator removed.' });
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

// The design's danger reds; no matching variables exist yet.
$remove-danger: #C0392B;
$remove-danger-light: #FDECEA;

// The confirm popup is rendered by SweetAlert at the end of <body>, so these can't be scoped.
.swal2-popup.remove-authenticator-popup {
  width: 460px;
  padding: 2rem;
  border-radius: 6px;
  font-family: inherit;

  .remove-authenticator-popup__icon {
    width: 4rem;
    height: 4rem;
    margin: 0 auto 1rem;
    border: 0;
    border-radius: 50%;
    background: $remove-danger-light;
    color: $remove-danger;
    font-size: 1.5rem;

    .swal2-icon-content {
      font-size: 1.5rem;
    }
  }

  .remove-authenticator-popup__title {
    padding: 0;
    margin: 0 0 0.5rem;
    font-size: 1.375rem;
    font-weight: 700;
    color: $tertiary-dark;
  }

  .remove-authenticator-popup__text {
    margin: 0;
    padding: 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: $tertiary-muted;
  }

  .remove-authenticator-popup__actions {
    gap: 0.5rem;
    margin: 1.5rem 0 0;
  }

  .remove-authenticator-popup__cancel,
  .remove-authenticator-popup__confirm {
    height: 2.5rem;
    padding: 0 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 700;
  }

  .remove-authenticator-popup__cancel {
    border: 1px solid $border-color;
    background: $tertiary-light-gray;
    color: $tertiary-dark;
  }

  .remove-authenticator-popup__confirm {
    border: 1px solid $remove-danger;
    background: $remove-danger;
    color: #fff;
  }
}
</style>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.account-mfa {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid $border-color;
}
.account-mfa__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}
.account-mfa__header :deep(.account-mfa__add) {
  flex-shrink: 0;
  white-space: nowrap;
}
.account-mfa__title {
  margin: 0 0 0.25rem;
  font-size: 1.0625rem;
  font-weight: 700;
  color: $tertiary-dark;
}
.account-mfa__description {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: $tertiary-muted;
}
.account-mfa__list {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid $border-color;
  border-radius: 6px;
}
.account-mfa__item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  & + & {
    border-top: 1px solid $border-color;
  }
}
.account-mfa__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: $secondary-light;
  color: $secondary-color;
}
.account-mfa__text {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 2px;
  min-width: 0;
}
.account-mfa__name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: $tertiary-dark;
}
.account-mfa__meta {
  font-size: 0.8125rem;
  color: $tertiary-muted;
}
.account-mfa__badge {
  padding: 2px 0.625rem;
  border: 1px solid $border-color;
  border-radius: 10px;
  background: $tertiary-light-gray;
  font-size: 0.75rem;
  font-weight: 700;
  color: $tertiary-muted;
  white-space: nowrap;
}
.account-mfa__remove {
  padding: 0;
  border: 0;
  background: none;
  font-size: 0.875rem;
  font-weight: 700;
  color: $error-dark;
  text-decoration: underline;
}
</style>
